import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import type { AuthController } from "@nasam/core";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeaderClose,
  DialogTitle,
  LoginLinearIcon,
  LogoutLinearIcon,
} from "@nasam/ui";
import { authController as defaultAuthController } from "../auth";

export interface LogoutConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly authController?: AuthController | undefined;
}

export function LogoutConfirmDialog({
  open,
  onOpenChange,
  authController = defaultAuthController,
}: LogoutConfirmDialogProps) {
  const { t, i18n } = useTranslation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Same swap as the rail's own logout button (see NavRail) -- a CSS mirror
  // had no visible effect on this glyph, so LTR/RTL each get a different,
  // already-mirrored Solar icon instead.
  const LogoutIcon = i18n.dir() === "rtl" ? LogoutLinearIcon : LoginLinearIcon;

  const handleConfirm = async () => {
    setIsLoggingOut(true);
    try {
      await authController.logout("manual");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (isLoggingOut) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        closeLabel={t("common:close")}
        showCloseButton={false}
        onEscapeKeyDown={(e) => {
          if (isLoggingOut) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isLoggingOut) e.preventDefault();
        }}
        className="gap-4 rounded-lg border-0 p-0 pb-4 sm:max-w-lg [--focus-ring:var(--color-badge-text-red)]"
      >
        <div className="flex h-16 items-center justify-between rounded-t-lg bg-badge-text-red px-4">
          <div className="flex items-center gap-2">
            <div className="flex size-10 items-center justify-center rounded-lg bg-badge-bg-light-red">
              <LogoutIcon className="size-6 text-badge-text-red" />
            </div>
            <DialogTitle className="text-primary-button-text">
              {t("common:logout")}
            </DialogTitle>
          </div>
          <DialogHeaderClose disabled={isLoggingOut} label={t("common:close")} />
        </div>

        <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
          <p className="text-body-l-b text-badge-text-red">
            {t("common:areYouSureToLogout")}
          </p>
          <p className="text-body-l-r text-text-placeholder">
            {t("common:youWillBeLoggedOut")}
          </p>
        </div>

        <div className="flex flex-col gap-3 px-4 sm:flex-row sm:gap-6 sm:rtl:flex-row-reverse">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            data-testid="logout-cancel-button"
            disabled={isLoggingOut}
            onClick={() => onOpenChange(false)}
            className="w-full border-0 bg-transparent shadow-none hover:bg-container-hover sm:flex-1"
          >
            <ArrowLeft className="rtl:rotate-180" />
            {t("common:cancel")}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            data-testid="logout-confirm-button"
            disabled={isLoggingOut}
            onClick={() => void handleConfirm()}
            className="w-full bg-badge-text-red hover:bg-badge-text-red-hover disabled:bg-badge-text-red disabled:opacity-60 sm:flex-1"
          >
            {isLoggingOut && <Loader2 className="size-4 animate-spin" />}
            {t("common:logout")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
